import React from 'react';
import { ROUTES } from './routeConstants.js';

// Lazy load components for better performance
const MainScreen = React.lazy(() => import('../screens/MainScreen.js'));
const Intro = React.lazy(() => import('../components/Intro.js'));

const FastStart = React.lazy(() => import('../components/FastStart.js'));
const PersonalizedPlanQuiz = React.lazy(() => import('../screens/PersonalizedPlanQuiz.js'));
const PersonalizedPlan = React.lazy(() => import('../screens/PersonalizedPlan.js'));
const Membership = React.lazy(() => import('../components/Membership.js'));
const VIPMember = React.lazy(() => import('../components/VIPMember.js'));
const ThankYou = React.lazy(() => import('../components/ThankYou.js'));
const Challenges = React.lazy(() => import('../screens/Challenges.js'));
const ActiveChallenge = React.lazy(() => import('../screens/ActiveChallenge.js'));
const OnboardingScreen = React.lazy(() => import('../screens/OnboardingScreen.js'));
const LoginScreen = React.lazy(() => import('../screens/LoginScreen.js'));
const SkillsScreen = React.lazy(() => import('../screens/SkillsScreen.js'));
const TrainerScreen = React.lazy(() => import('../screens/TrainerScreen.js'));
const RateScreen = React.lazy(() => import('../screens/RateScreen.js'));
const SubscriptionScreen = React.lazy(() => import('../screens/SubscriptionScreen.js'));
const SubscriptionSuccess = React.lazy(() => import('../screens/SubscriptionSuccess.js'));
const SubscriptionCancel = React.lazy(() => import('../screens/SubscriptionCancel.js'));
const EditProfileScreen = React.lazy(() => import('../screens/EditProfileScreen.js'));
const ForgotPasswordScreen = React.lazy(() => import('../screens/ForgotPasswordScreen.js'));
const PrivacyPolicyScreen = React.lazy(() => import('../screens/PrivacyPolicyScreen.js'));
const TermsOfServiceScreen = React.lazy(() => import('../screens/TermsOfServiceScreen.js'));
const DogDashboard = React.lazy(() => import('../screens/DogDashboardScreen.js'));
// AuthRedirect removed - not being used in current flow
const Purchases = React.lazy(() => import('../screens/Purchases.js'));
const HelpCenterScreen = React.lazy(() => import('../screens/HelpCenterScreen.js'));
const FAQScreen = React.lazy(() => import('../screens/FAQScreen.js'));
const CookiePolicyScreen = React.lazy(() => import('../screens/CookiePolicyScreen.js'));
const AccessibilityScreen = React.lazy(() => import('../screens/AccessibilityScreen.js'));
const SitemapScreen = React.lazy(() => import('../screens/SitemapScreen.js'));
const UnsubscribeScreen = React.lazy(() => import('../screens/UnsubscribeScreen.js'));
const EmailTestScreen = React.lazy(() => import('../screens/EmailTestScreen.js'));
const MyMembership = React.lazy(() => import('../screens/MyMembership.js'));
const AddDogScreen = React.lazy(() => import('../screens/AddDogScreen.js'));
const NotFoundScreen = React.lazy(() => import('../screens/NotFoundScreen.js'));

// Route configuration with access levels and metadata
export const routeConfig = [
  // Public Routes
  {
    path: ROUTES.HOME,
    component: MainScreen,
    title: 'Doglingo - AI Dog Training',
    description: 'Train and track your dog\'s skills with AI',
    public: true
  },
  {
    path: ROUTES.DOG_TRAINING_CHALLENGE,
    component: Intro,
    title: '3-Day Dog Training Challenge',
    description: 'Start your dog training journey',
    public: true
  },

  {
    path: ROUTES.MEMBERSHIP,
    component: Membership,
    title: 'Membership',
    description: 'Choose your membership plan',
    public: true
  },
  {
    path: ROUTES.VIP_MEMBER,
    component: VIPMember,
    title: 'VIP Membership',
    description: 'Exclusive VIP benefits',
    public: true
  },
  {
    path: ROUTES.THANK_YOU,
    component: ThankYou,
    title: 'Thank You',
    description: 'Thank you for joining',
    public: true
  },
  {
    path: ROUTES.CHALLENGES,
    component: Challenges,
    title: 'Training Challenges',
    description: 'Available training challenges',
    public: true
  },
  {
    path: ROUTES.CHALLENGE,
    component: Intro,
    title: '3-Day Challenge',
    description: 'Join the 3-Day Dog Training Challenge',
    public: true
  },
  {
    path: ROUTES.ONBOARDING,
    component: OnboardingScreen,
    title: 'Get Started',
    description: 'Onboard your dog',
    public: true
  },
  {
    path: ROUTES.LOGIN,
    component: LoginScreen,
    title: 'Login',
    description: 'Sign in to your account',
    public: true
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    component: ForgotPasswordScreen,
    title: 'Forgot Password',
    description: 'Reset your password',
    public: true
  },

  // Private Routes (require authentication)
  {
    path: ROUTES.FAST_START,
    component: FastStart,
    title: 'Fast Start',
    description: 'Quick start training',
    private: true
  },
  {
    path: ROUTES.PERSONALIZED_PLAN_QUIZ,
    component: PersonalizedPlanQuiz,
    title: 'Personalized Plan Quiz',
    description: 'Answer questions for your custom plan',
    private: true
  },
  {
    path: ROUTES.PERSONALIZED_PLAN,
    component: PersonalizedPlan,
    title: 'Your Personalized Plan',
    description: 'Custom training plan for your dog',
    private: true
  },
  {
    path: ROUTES.PURCHASES,
    component: Purchases,
    title: 'Purchase History',
    description: 'View your purchase history',
    private: true
  },
  {
    path: ROUTES.ACTIVE_CHALLENGE,
    component: ActiveChallenge,
    title: 'Active Challenge',
    description: 'Your current training challenge',
    private: true
  },
  {
    path: ROUTES.CHALLENGE_TIMELINE,
    component: Challenges,
    title: 'Challenge Timeline',
    description: 'Your challenge progress',
    private: true
  },
  {
    path: ROUTES.DASHBOARD,
    component: DogDashboard,
    title: 'Dashboard',
    description: 'Your training dashboard',
    private: true,
    requireAccountSetup: true
  },
  {
    path: ROUTES.SKILLS,
    component: SkillsScreen,
    title: 'Training Skills',
    description: 'Available training skills',
    private: true,
    suggestDogSetup: true
  },
  {
    path: ROUTES.ADD_DOG,
    component: AddDogScreen,
    title: 'Add Dog Profile',
    description: 'Add your dog\'s profile',
    private: true
  },
  {
    path: ROUTES.TRAINER,
    component: TrainerScreen,
    title: 'AI Trainer',
    description: 'Train with AI assistance',
    private: true,
    suggestDogSetup: true
  },
  {
    path: ROUTES.RATE,
    component: RateScreen,
    title: 'Rate Your Experience',
    description: 'Share your feedback',
    private: true
  },
  {
    path: ROUTES.SUBSCRIPTION,
    component: SubscriptionScreen,
    title: 'Subscription',
    description: 'Manage your subscription',
    private: true
  },
  {
    path: ROUTES.SUBSCRIPTION_SUCCESS,
    component: SubscriptionSuccess,
    title: 'Subscription Success',
    description: 'Subscription activated',
    private: true
  },
  {
    path: ROUTES.MY_MEMBERSHIP,
    component: MyMembership,
    title: 'My Membership',
    description: 'Manage your membership',
    private: true
  },
  {
    path: ROUTES.SUBSCRIPTION_CANCEL,
    component: SubscriptionCancel,
    title: 'Subscription Cancelled',
    description: 'Subscription cancelled',
    private: true
  },
  {
    path: ROUTES.EDIT_PROFILE,
    component: EditProfileScreen,
    title: 'Edit Profile',
    description: 'Update your profile',
    private: true
  },

  // Legal Pages
  {
    path: ROUTES.PRIVACY_POLICY,
    component: PrivacyPolicyScreen,
    title: 'Privacy Policy',
    description: 'Our privacy policy',
    public: true
  },
  {
    path: ROUTES.TERMS_OF_SERVICE,
    component: TermsOfServiceScreen,
    title: 'Terms of Service',
    description: 'Our terms of service',
    public: true
  },
  {
    path: ROUTES.COOKIE_POLICY,
    component: CookiePolicyScreen,
    title: 'Cookie Policy',
    description: 'Our cookie policy',
    public: true
  },
  {
    path: ROUTES.ACCESSIBILITY,
    component: AccessibilityScreen,
    title: 'Accessibility',
    description: 'Accessibility information',
    public: true
  },

  // Support Pages
  {
    path: ROUTES.HELP,
    component: HelpCenterScreen,
    title: 'Help Center',
    description: 'Get help and support',
    public: true
  },
  {
    path: ROUTES.FAQ,
    component: FAQScreen,
    title: 'FAQ',
    description: 'Frequently asked questions',
    public: true
  },
  {
    path: ROUTES.SITEMAP,
    component: SitemapScreen,
    title: 'Sitemap',
    description: 'Site navigation',
    public: true
  },
  {
    path: ROUTES.UNSUBSCRIBE,
    component: UnsubscribeScreen,
    title: 'Unsubscribe',
    description: 'Unsubscribe from emails',
    public: true
  },

  // Auth Routes - Removed AuthRedirect as it's not being used

  // Development/Testing Routes
  {
    path: ROUTES.EMAIL_TEST,
    component: EmailTestScreen,
    title: 'Email Test',
    description: 'Test email functionality',
    public: true,
    development: true
  },

  // Fallback
  {
    path: ROUTES.NOT_FOUND,
    component: NotFoundScreen,
    title: 'Page Not Found',
    description: 'The page you\'re looking for doesn\'t exist',
    public: true
  }
];

// Helper function to get route config by path
export const getRouteConfig = (path) => {
  return routeConfig.find(route => route.path === path);
};

// Helper function to get all public routes
export const getPublicRoutes = () => {
  return routeConfig.filter(route => route.public);
};

// Helper function to get all private routes
export const getPrivateRoutes = () => {
  return routeConfig.filter(route => route.private);
}; 
